/**
 * Types for scheduled analysis
 * Supports device-based scheduling with geofence notifications
 */

export enum ScheduleType {
  ONCE = 'once',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly'
}

export enum DateRangeType {
  LAST_7_DAYS = 'last_7_days',
  LAST_14_DAYS = 'last_14_days',
  LAST_30_DAYS = 'last_30_days',
  LAST_90_DAYS = 'last_90_days',
  THIS_WEEK = 'this_week',
  LAST_WEEK = 'last_week',
  THIS_MONTH = 'this_month',
  LAST_MONTH = 'last_month',
  CUSTOM = 'custom'
}

export enum AzureFunctionStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

export interface ScheduledAnalysisConfig {
  device_type: string;
  analysis_type: string;
  parameters: Record<string, any>;
  processing_mode?: 'single' | 'individual';  // Single report or individual per device
  students?: string[];  // Legacy field for backward compatibility
}

export interface ScheduledAnalysis {
  id: number;
  name: string;
  description?: string;
  user_id: string;
  analysis_config: ScheduledAnalysisConfig;
  schedule_type: ScheduleType;
  schedule_time: string;  // "14:30:00" format
  schedule_days?: string;  // "MON,WED,FRI"
  timezone: string;
  date_range_type: DateRangeType;
  custom_start_date?: string;
  custom_end_date?: string;
  is_active: boolean;
  last_run_at?: string;
  last_run_status?: string;  // success, failed, running
  last_run_result?: Record<string, any>;  // Analysis result summary
  next_run_at?: string;
  run_count: number;
  last_job_id?: number;
  last_error?: string;
  consecutive_failures: number;
  created_at: string;
  updated_at?: string;
  
  // Device-based scheduling
  device_ids: string[];  // Watchlist device registration IDs
  
  // Geofence notification settings
  geofence_notification_enabled: boolean;
  notification_geofence_ids: number[];
  notification_email?: string;
  notification_webhook_url?: string;
  last_notification_at?: string;
  
  // Report completion notification
  report_notification_enabled: boolean;
  
  // Azure Function tracking
  azure_function_job_id?: string;
  azure_function_status?: AzureFunctionStatus;
}

export interface ScheduledAnalysisCreate {
  name: string;
  description?: string;
  analysis_config: ScheduledAnalysisConfig;
  schedule_type: ScheduleType;
  schedule_time: string;
  schedule_days?: string;
  timezone?: string;
  date_range_type: DateRangeType;
  custom_start_date?: string;
  custom_end_date?: string;
  is_active?: boolean;
  
  // Device-based scheduling
  device_ids: string[];
  
  // Geofence notification settings
  geofence_notification_enabled?: boolean;
  notification_geofence_ids?: number[];
  notification_email?: string;
  notification_webhook_url?: string;
  
  // Report completion notification
  report_notification_enabled?: boolean;
}

export interface ScheduledAnalysisUpdate {
  name?: string;
  description?: string;
  analysis_config?: ScheduledAnalysisConfig;
  schedule_type?: ScheduleType;
  schedule_time?: string;
  schedule_days?: string;
  timezone?: string;
  date_range_type?: DateRangeType;
  custom_start_date?: string;
  custom_end_date?: string;
  is_active?: boolean;
  
  // Device-based scheduling
  device_ids?: string[];
  
  // Geofence notification settings
  geofence_notification_enabled?: boolean;
  notification_geofence_ids?: number[];
  notification_email?: string;
  notification_webhook_url?: string;
  
  // Report completion notification
  report_notification_enabled?: boolean;
}

/**
 * Watchlist device for selection in scheduler
 */
export interface WatchlistDevice {
  id: number;
  registration_id: string;
  name?: string;
  notes?: string;
  tags: string[];
  created_by?: string;
  created_at: string;
  updated_at: string;
  course_id?: string;
  active: boolean;
}

/**
 * Geofence for selection in scheduler
 */
export interface GeofenceOption {
  id: number;
  name: string;
  description?: string;
  active: boolean;
}

